import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader } from '../../components/common';
import { FiPlus, FiX } from 'react-icons/fi';
import { fetchMyCalls, cancelStockCall, StockCall } from '../../api';
import routes from '../../utils/routes';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  OPEN: { bg: '#f59e0b20', text: '#f59e0b' },
  TARGET_HIT: { bg: '#22c55e20', text: '#22c55e' },
  SL_HIT: { bg: '#ef444420', text: '#ef4444' },
  EXPIRED: { bg: '#6b728020', text: '#6b7280' },
  CANCELLED: { bg: '#9ca3af20', text: '#9ca3af' },
};

const ExpertCallsManagement: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(true);
  const [calls, setCalls] = useState<StockCall[]>([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState<'OPEN' | 'RESOLVED' | 'ALL'>('OPEN');
  const [cancelModalId, setCancelModalId] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadCalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadCalls = async () => {
    setLoading(true);
    try {
      const statusFilter = tab === 'OPEN' ? 'OPEN' : tab === 'RESOLVED' ? undefined : undefined;
      const result = await fetchMyCalls(statusFilter);
      let filtered = result.calls;
      if (tab === 'RESOLVED') {
        filtered = filtered.filter(c => ['TARGET_HIT', 'SL_HIT', 'EXPIRED', 'CANCELLED'].includes(c.status));
      }
      setCalls(filtered);
      setTotal(result.total);
    } catch {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModalId) return;
    setCancelling(true);
    try {
      await cancelStockCall(cancelModalId);
      setCancelModalId(null);
      loadCalls();
    } catch {
      // error handled in api layer
    } finally {
      setCancelling(false);
    }
  };

  // Compute summary stats from all calls
  const targetHitCount = calls.filter(c => c.status === 'TARGET_HIT').length;
  const resolvedCalls = calls.filter(c => ['TARGET_HIT', 'SL_HIT', 'EXPIRED'].includes(c.status));
  const accuracy = resolvedCalls.length > 0
    ? ((targetHitCount / resolvedCalls.length) * 100).toFixed(1)
    : '0.0';
  const avgReturn = resolvedCalls.length > 0
    ? (resolvedCalls.reduce((sum, c) => sum + (c.return_pct || 0), 0) / resolvedCalls.length).toFixed(1)
    : '0.0';

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const tabs = [
    { key: 'OPEN' as const, label: 'Open' },
    { key: 'RESOLVED' as const, label: 'Resolved' },
    { key: 'ALL' as const, label: 'All' },
  ];

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: colors.backgroundColor }}>
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Stock Calls" />

          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: colors.inputBackground }}>
              <CText type="B24" color={colors.primary}>{total}</CText>
              <CText type="r12" color={colors.grayScale500}>Total Calls</CText>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: colors.inputBackground }}>
              <CText type="B24" color="#22c55e">{accuracy}%</CText>
              <CText type="r12" color={colors.grayScale500}>Accuracy</CText>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: colors.inputBackground }}>
              <CText type="B24" color={parseFloat(avgReturn) >= 0 ? '#22c55e' : '#ef4444'}>
                {parseFloat(avgReturn) >= 0 ? '+' : ''}{avgReturn}%
              </CText>
              <CText type="r12" color={colors.grayScale500}>Avg Return</CText>
            </div>
          </div>

          {/* Log New Call Button */}
          <button
            onClick={() => navigate(routes.expertNewCall)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl mb-6"
            style={{ backgroundColor: colors.primary }}
          >
            {React.createElement(FiPlus as any, { size: 18, color: '#fff' })}
            <CText type="M14" color="#fff">Log New Call</CText>
          </button>

          {/* Tab Bar */}
          <div
            className="flex rounded-full p-1 mb-5"
            style={{ backgroundColor: colors.inputBackground }}
          >
            {tabs.map(t => (
              <button
                key={t.key}
                className="flex-1 py-2.5 rounded-full text-sm font-medium transition-all duration-200"
                style={{
                  backgroundColor: tab === t.key ? colors.primary : 'transparent',
                  color: tab === t.key ? '#fff' : colors.grayScale400,
                }}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Call List */}
          {loading ? (
            <div className="flex justify-center py-16"><CLoader /></div>
          ) : calls.length === 0 ? (
            <div className="text-center py-12">
              <CText type="r14" color={colors.grayScale500}>
                {tab === 'OPEN' ? 'No open calls. Log your first stock call!' : 'No calls found.'}
              </CText>
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map(call => {
                const sc = STATUS_COLORS[call.status] || STATUS_COLORS.OPEN;
                return (
                  <div
                    key={call.id}
                    className="rounded-xl p-4"
                    style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
                  >
                    {/* Header: ticker + status badge */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span
                          className="px-2 py-0.5 rounded text-xs font-bold"
                          style={{ backgroundColor: call.call_type === 'BUY' ? '#22c55e20' : '#ef444420', color: call.call_type === 'BUY' ? '#22c55e' : '#ef4444' }}
                        >
                          {call.call_type}
                        </span>
                        <CText type="B16" color={colors.textColor}>{call.ticker_symbol}</CText>
                        <CText type="r12" color={colors.grayScale500}>{call.exchange}</CText>
                      </div>
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: sc.bg, color: sc.text }}
                      >
                        {call.status.replace('_', ' ')}
                      </span>
                    </div>

                    <CText type="r12" color={colors.grayScale500} className="mb-2">{call.company_name}</CText>

                    {/* Prices */}
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div>
                        <CText type="r10" color={colors.grayScale400}>Entry</CText>
                        <CText type="M12" color={colors.textColor}>Rs.{call.entry_price}</CText>
                      </div>
                      <div>
                        <CText type="r10" color={colors.grayScale400}>Target</CText>
                        <CText type="M12" color="#22c55e">Rs.{call.target_price}</CText>
                      </div>
                      <div>
                        <CText type="r10" color={colors.grayScale400}>Stop Loss</CText>
                        <CText type="M12" color="#ef4444">Rs.{call.stop_loss_price}</CText>
                      </div>
                    </div>

                    {/* Outcome row for resolved calls */}
                    {call.outcome_price && (
                      <div className="flex items-center gap-3 mb-2 px-2 py-1.5 rounded-lg" style={{ backgroundColor: colors.backgroundColor }}>
                        <CText type="r12" color={colors.grayScale500}>
                          Outcome: Rs.{call.outcome_price}
                        </CText>
                        {call.return_pct !== null && (
                          <CText type="M12" color={call.return_pct >= 0 ? '#22c55e' : '#ef4444'}>
                            {call.return_pct >= 0 ? '+' : ''}{call.return_pct}%
                          </CText>
                        )}
                      </div>
                    )}

                    {/* Footer: date + cancel */}
                    <div className="flex items-center justify-between mt-1">
                      <CText type="r10" color={colors.grayScale400}>
                        {formatDate(call.created_at)} — expires {formatDate(call.expires_at)}
                      </CText>
                      {call.status === 'OPEN' && (
                        <button
                          onClick={() => setCancelModalId(call.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded"
                          style={{ color: '#ef4444' }}
                        >
                          {React.createElement(FiX as any, { size: 12 })}
                          <span className="text-xs">Cancel</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {cancelModalId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl p-6 w-full max-w-sm" style={{ backgroundColor: colors.backgroundColor }}>
            <CText type="B18" color={colors.textColor} style={{ marginBottom: 8 }}>Cancel this call?</CText>
            <CText type="r14" color={colors.grayScale500} style={{ marginBottom: 24 }}>
              Cancelled calls remain visible in your history for transparency.
            </CText>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModalId(null)}
                className="flex-1 py-3 rounded-xl border"
                style={{ borderColor: colors.borderColor }}
              >
                <CText type="M14" color={colors.textColor}>Keep</CText>
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 py-3 rounded-xl"
                style={{ backgroundColor: '#ef4444', opacity: cancelling ? 0.6 : 1 }}
              >
                <CText type="M14" color="#fff">{cancelling ? 'Cancelling...' : 'Cancel Call'}</CText>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertCallsManagement;
